import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { SelectChangeEvent } from "@mui/material/Select";

import {
  CalendarTodayOutlined,
  ClearOutlined,
  DownloadOutlined,
  FilterAltOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";

import * as XLSX from "xlsx";

import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";

import { GetMonthlyAttendanceReport } from "../../../services/asai";

/* ============================================================
   TYPES
   ============================================================ */

type EmployeeType =
  | "payroll cnc"
  | "contract cnc"
  | "apprentice cnc"
  | "payroll flex"
  | "contract flex"
  | "apprentice flex";

type EmployeeTypeFilter = EmployeeType | "all";

type MonthlyAttendanceDay = {
  dayNumber: number;
  date: string;
  day: string;
  inTime: string;
  outTime: string;
  status: string;
  hours: string;
  overtime: number;
};

type MonthlyAttendanceEmployee = {
  personId: string;
  personName: string;
  employeeType: string;
  shift: string;
  contractor: string;
  days: MonthlyAttendanceDay[];
};

type ColumnFilterMap = Record<string, string>;

/* ============================================================
   CONSTANTS
   ============================================================ */

const EMPLOYEE_TYPES: Array<{
  value: EmployeeType;
  label: string;
}> = [
  {
    value: "payroll cnc",
    label: "Payroll CNC",
  },
  {
    value: "contract cnc",
    label: "Contract CNC",
  },
  {
    value: "apprentice cnc",
    label: "Apprentice CNC",
  },
  {
    value: "payroll flex",
    label: "Payroll FLEX",
  },
  {
    value: "contract flex",
    label: "Contract FLEX",
  },
  {
    value: "apprentice flex",
    label: "Apprentice FLEX",
  },
];

/*
 * Payroll CNC / Contract CNC
 */
const PAYROLL_CONTRACT_CNC_SHIFTS = [
  "Shift 1 (8:00 to 6:30)",
  "Shift 2 (6:30 to 5:00)",
  "Shift 3 (8:00 to 6:30)",
  "Shift 4 (11:00 to 9:30)",
  "Shift 5 (9:30 to 8:00)",
];

/*
 * Apprentice CNC
 */
const APPRENTICE_CNC_SHIFTS = [
  "Shift 1 (8:00 to 6:30)",
  "Shift 2 (6:30 to 5:00)",
  "Shift A (8:00 to 6:30)",
  "Shift B (11:00 to 9:30)",
  "Shift C (9:30 to 8:00)",
];

/*
 * FLEX
 */
const FLEX_SHIFTS = [
  "Shift 1 (8:00 to 4:30)",
  "Shift 2 (4:30 to 1:00)",
  "Shift 3 (1:00 to 8:00)",
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* ============================================================
   HELPERS
   ============================================================ */

const getCurrentYear = () => new Date().getFullYear();

const getCurrentMonth = () => new Date().getMonth() + 1;

const getShiftOptions = (employeeType: EmployeeType): string[] => {
  switch (employeeType) {
    case "payroll cnc":
    case "contract cnc":
      return PAYROLL_CONTRACT_CNC_SHIFTS;

    case "apprentice cnc":
      return APPRENTICE_CNC_SHIFTS;

    case "payroll flex":
    case "contract flex":
    case "apprentice flex":
      return FLEX_SHIFTS;

    default:
      return [];
  }
};

const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

const normalizeReportResponse = (
  response: any,
): MonthlyAttendanceEmployee[] => {
  const root = response?.data?.data ?? response?.data ?? {};

  const rawResult = Array.isArray(root?.result)
    ? root.result
    : Array.isArray(root?.Result)
    ? root.Result
    : Array.isArray(response?.data)
    ? response.data
    : [];

  return rawResult.map((item: any) => {
    const rawDays = Array.isArray(item?.days)
      ? item.days
      : Array.isArray(item?.Days)
      ? item.Days
      : [];

    return {
      personId: String(item?.personId ?? item?.PersonId ?? "").trim(),

      personName: String(item?.personName ?? item?.PersonName ?? "").trim(),

      employeeType: String(
        item?.employeeType ??
          item?.EmployeeType ??
          item?.organization ??
          item?.Organization ??
          item?.typeOfEmployee ??
          item?.TypeOfEmployee ??
          "",
      ).trim(),

      shift: String(
        item?.shift ?? item?.Shift ?? item?.shiftName ?? item?.ShiftName ?? "",
      ).trim(),

      contractor: String(item?.contractor ?? item?.Contractor ?? "").trim(),

      days: rawDays.map((day: any) => ({
        dayNumber: Number(day?.dayNumber ?? day?.DayNumber ?? 0),

        date: String(day?.date ?? day?.Date ?? ""),

        day: String(day?.day ?? day?.Day ?? ""),

        inTime: String(day?.inTime ?? day?.InTime ?? "-"),

        outTime: String(day?.outTime ?? day?.OutTime ?? "-"),

        status: String(day?.status ?? day?.Status ?? "No Data"),

        hours: String(day?.hours ?? day?.Hours ?? ""),

        overtime: Number(day?.overtime ?? day?.Overtime ?? 0),
      })),
    };
  });
};

const getStatusColor = (
  status: string,
): "success" | "warning" | "error" | "info" | "default" => {
  const value = status?.trim().toLowerCase();

  switch (value) {
    case "full day":
    case "present":
    case "p":
      return "success";

    case "half day":
    case "h":
      return "warning";

    case "absent":
    case "a":
      return "error";

    case "missing punch":
    case "m":
      return "warning";

    case "off day":
    case "sunday":
    case "o":
    case "op":
      return "info";

    default:
      return "default";
  }
};

const getStatusLabel = (status: string) => {
  if (!status || status.trim() === "" || status.trim() === "-") {
    return "No Data";
  }

  return status;
};

const getInitials = (name: string) => {
  if (!name?.trim()) {
    return "E";
  }

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

/* ============================================================
   PAGE
   ============================================================ */

const EmployeeMonthlyAttendancePage: React.FC = () => {
  /* ========================================================
             MAIN FILTER STATE
             ======================================================== */

  const [employeeType, setEmployeeType] = useState<EmployeeTypeFilter>("all");

  const [month, setMonth] = useState<number>(getCurrentMonth());

  const [year, setYear] = useState<number>(getCurrentYear());

  const [shift, setShift] = useState<string>("all");

  /* ========================================================
             REPORT STATE
             ======================================================== */

  const [rows, setRows] = useState<MonthlyAttendanceEmployee[]>([]);

  const [loading, setLoading] = useState(false);

  // React StrictMode can replay mount effects in development. Keep the
  // automatic first report request to a single execution per mounted page.
  const initialLoadStarted = useRef(false);

  const [error, setError] = useState("");

  const [searchText, setSearchText] = useState("");

  /* ========================================================
             PAGINATION
             ======================================================== */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ========================================================
             INLINE COLUMN FILTER STATE
  
             activeFilterKey:
             personId
             personName
             day-1-in
             day-1-out
             day-1-status
             etc.
             ======================================================== */

  const [columnFilters, setColumnFilters] = useState<ColumnFilterMap>({});

  const [activeFilterKey, setActiveFilterKey] = useState("");

  /* ========================================================
             SHIFT OPTIONS
             ======================================================== */

  const allShiftOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...PAYROLL_CONTRACT_CNC_SHIFTS,
          ...APPRENTICE_CNC_SHIFTS,
          ...FLEX_SHIFTS,
        ]),
      ),
    [],
  );

  const shiftOptions = useMemo(
    () =>
      employeeType === "all" ? allShiftOptions : getShiftOptions(employeeType),
    [employeeType, allShiftOptions],
  );

  useEffect(() => {
    if (shift !== "all" && !shiftOptions.includes(shift)) {
      setShift("all");
    }
  }, [employeeType, shiftOptions, shift]);

  /* ========================================================
             DAYS
             ======================================================== */

  const daysInMonth = useMemo(() => getDaysInMonth(year, month), [year, month]);

  const dayNumbers = useMemo(
    () =>
      Array.from(
        {
          length: daysInMonth,
        },
        (_, index) => index + 1,
      ),
    [daysInMonth],
  );

  /* ========================================================
             DYNAMIC COLUMN WIDTHS
  
             Closed column:
               Person ID   = 90
               Person Name = 220
               Day field   = 60
  
             Open filter:
               Person ID   = 180
               Person Name = 300
               Day field   = 125
  
             This makes the input visible and lets the user read
             whatever has been typed.
             ======================================================== */

  const personIdWidth = activeFilterKey === "personId" ? 180 : 90;

  const personNameWidth = activeFilterKey === "personName" ? 300 : 220;

  const getDayFieldWidth = (day: number, field: "in" | "out" | "status") =>
    activeFilterKey === `day-${day}-${field}` ? 125 : 60;

  const getDayGroupWidth = (day: number) =>
    getDayFieldWidth(day, "in") +
    getDayFieldWidth(day, "out") +
    getDayFieldWidth(day, "status");

  const employeeTypeWidth = 145;
  const shiftWidth = 210;

  const tableMinWidth =
    personIdWidth +
    personNameWidth +
    employeeTypeWidth +
    shiftWidth +
    dayNumbers.reduce((total, day) => total + getDayGroupWidth(day), 0);

  /* ========================================================
             LOAD REPORT
             ======================================================== */

  const handleLoad = async () => {
    if (!year || year < 2000 || year > 2100) {
      setError("Please enter a valid year.");

      return;
    }

    if (month < 1 || month > 12) {
      setError("Please select a valid month.");

      return;
    }

    if (!employeeType) {
      setError("Please select employee type.");

      return;
    }

    if (!shift) {
      setError("Please select shift.");

      return;
    }

    setLoading(true);
    setError("");

    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const abortController = new AbortController();
    let requestTimedOut = false;

    try {
      const requestParameters = {
        year,
        month,
        employeeType: employeeType === "all" ? "all" : employeeType,
        shift: shift === "all" ? "all" : shift,
      };

      console.info(
        "[MonthlyAttendanceReport] Request started",
        requestParameters,
      );

      // Abort the actual Axios/XHR request at the timeout. Promise.race alone only
      // stops waiting in this component; it leaves the network request alive and
      // can leave application-wide Axios loading indicators running forever.
      timeoutHandle = setTimeout(() => {
        requestTimedOut = true;
        console.error(
          "[MonthlyAttendanceReport] Request timed out; aborting HTTP request.",
        );
        setError(
          "Monthly attendance request did not respond within 70 seconds. " +
            "The HTTP request was aborted. Check the backend console for the last " +
            "MonthlyAttendanceReport log entry.",
        );
        setLoading(false);
        abortController.abort();
      }, 70_000);

      // IMPORTANT: GetMonthlyAttendanceReport in src/services/asai must accept
      // the optional second argument and pass it through as Axios `signal`.
      const response = await GetMonthlyAttendanceReport(
        requestParameters,
        abortController.signal,
      );

      console.info("[MonthlyAttendanceReport] API response received", response);
      const parsed = normalizeReportResponse(response);

      setRows(parsed);
      setSearchText("");
      setColumnFilters({});
      setActiveFilterKey("");
      setPage(0);

      if (parsed.length === 0) {
        setError(
          "No employees found for the selected month, year and filters.",
        );
      }
    } catch (requestError: any) {
      console.error("Failed to load monthly attendance report:", requestError);

      setRows([]);

      if (requestTimedOut) {
        setError(
          "Monthly attendance request timed out after 70 seconds and was aborted. " +
            "Check the backend console for the last MonthlyAttendanceReport log entry.",
        );
      } else if (
        requestError?.name === "CanceledError" ||
        requestError?.code === "ERR_CANCELED"
      ) {
        setError("Monthly attendance request was cancelled.");
      } else {
        setError(
          requestError?.response?.data?.message ||
            requestError?.response?.data?.Message ||
            requestError?.message ||
            "Unable to load monthly attendance report.",
        );
      }
    } finally {
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
      setLoading(false);
    }
  };

  // Automatically load the current month/year for all employee types and shifts.
  // The filters can still be changed and submitted with Search afterwards.
  useEffect(() => {
    if (initialLoadStarted.current) {
      return;
    }

    initialLoadStarted.current = true;
    void handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========================================================
             GLOBAL SEARCH + COLUMN FILTERS
             ======================================================== */

  const filteredRows = useMemo(() => {
    const globalSearch = searchText.trim().toLowerCase();

    return rows.filter((employee) => {
      /* -----------------------------------------
                                 GLOBAL SEARCH
                                 ----------------------------------------- */

      if (globalSearch) {
        const globalMatch =
          employee.personId.toLowerCase().includes(globalSearch) ||
          employee.personName.toLowerCase().includes(globalSearch) ||
          employee.employeeType.toLowerCase().includes(globalSearch) ||
          employee.shift.toLowerCase().includes(globalSearch) ||
          employee.contractor?.toLowerCase().includes(globalSearch) ||
          employee.days.some(
            (day) =>
              day.inTime?.toLowerCase().includes(globalSearch) ||
              day.outTime?.toLowerCase().includes(globalSearch) ||
              day.status?.toLowerCase().includes(globalSearch) ||
              getStatusLabel(day.status || "")
                .toLowerCase()
                .includes(globalSearch),
          );

        if (!globalMatch) {
          return false;
        }
      }

      /* -----------------------------------------
                                 COLUMN FILTERS
      
                                 Every active column filter is ANDed.
                                 ----------------------------------------- */

      return Object.entries(columnFilters).every(([key, filterValue]) => {
        const search = filterValue.trim().toLowerCase();

        if (!search) {
          return true;
        }

        /* Person ID */
        if (key === "personId") {
          return employee.personId.toLowerCase().includes(search);
        }

        /* Person Name */
        if (key === "personName") {
          return employee.personName.toLowerCase().includes(search);
        }

        /* Employee Type */
        if (key === "employeeType") {
          return employee.employeeType.toLowerCase().includes(search);
        }

        /* Shift */
        if (key === "shift") {
          return employee.shift.toLowerCase().includes(search);
        }

        /* Day column */
        const match = key.match(/^day-(\d+)-(in|out|status)$/i);

        if (!match) {
          return true;
        }

        const dayNumber = Number(match[1]);

        const field = match[2].toLowerCase();

        const dayData = employee.days.find(
          (day) => day.dayNumber === dayNumber,
        );

        let value = "No Data";

        if (dayData) {
          if (field === "in") {
            value = dayData.inTime || "-";
          } else if (field === "out") {
            value = dayData.outTime || "-";
          } else {
            value = dayData.status || "No Data";
          }
        }

        return String(value).toLowerCase().includes(search);
      });
    });
  }, [rows, searchText, columnFilters]);

  /* ========================================================
             PAGED ROWS
             ======================================================== */

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, rowsPerPage, page]);

  /* ========================================================
             INLINE FILTER HELPERS
             ======================================================== */

  const toggleColumnFilter = (key: string) => {
    setActiveFilterKey((previous) => (previous === key ? "" : key));
  };

  const updateColumnFilter = (key: string, value: string) => {
    setColumnFilters((previous) => {
      const updated = {
        ...previous,
      };

      if (value.trim()) {
        updated[key] = value;
      } else {
        delete updated[key];
      }

      return updated;
    });

    setPage(0);
  };

  const clearColumnFilter = (key: string) => {
    setColumnFilters((previous) => {
      const updated = {
        ...previous,
      };

      delete updated[key];

      return updated;
    });

    setPage(0);
  };

  const clearAllColumnFilters = () => {
    setColumnFilters({});
    setActiveFilterKey("");
    setPage(0);
  };

  const activeColumnFilterCount = Object.keys(columnFilters).length;

  const hasActiveColumnFilter = (key: string) =>
    Boolean(columnFilters[key]?.trim());

  /* ========================================================
             INLINE FILTER ICON
             ======================================================== */

  const ColumnFilterButton = ({
    filterKey,
    label,
    size = 12,
  }: {
    filterKey: string;
    label: string;
    size?: number;
  }) => {
    const active = hasActiveColumnFilter(filterKey);

    const opened = activeFilterKey === filterKey;

    return (
      <IconButton
        type="button"
        size="small"
        aria-label={`Filter ${label}`}
        title={opened ? `Close ${label} filter` : `Filter ${label}`}
        onClick={(event) => {
          event.stopPropagation();

          toggleColumnFilter(filterKey);
        }}
        sx={{
          width: 20,
          height: 20,
          p: 0,
          flexShrink: 0,
          borderRadius: 0.75,
          color: active || opened ? "primary.main" : "text.secondary",

          "&:hover": {
            backgroundColor: "action.hover",
            color: "primary.main",
          },
        }}
      >
        <FilterAltOutlined sx={{ fontSize: 14 }} />
      </IconButton>
    );
  };

  /* ========================================================
             INLINE FILTER INPUT
  
             Reusable header input.
             ======================================================== */

  const InlineFilterInput = ({
    filterKey,
    placeholder = "Filter...",
    width,
    fontSize = "0.68rem",
  }: {
    filterKey: string;
    placeholder?: string;
    width: number;
    fontSize?: string;
  }) => {
    const value = columnFilters[filterKey] || "";

    return (
      <TextField
        autoFocus
        fullWidth
        variant="outlined"
        size="small"
        value={value}
        placeholder={placeholder}
        onChange={(event) => updateColumnFilter(filterKey, event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();

            setActiveFilterKey("");
          }
        }}
        InputProps={{
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                title="Clear filter"
                aria-label="Clear filter"
                onClick={(event) => {
                  event.stopPropagation();

                  clearColumnFilter(filterKey);
                }}
                sx={{
                  p: 0.15,
                  mr: -0.25,
                }}
              >
                <ClearOutlined
                  sx={{
                    fontSize: 14,
                  }}
                />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          width,
          maxWidth: "100%",
          "& .MuiInputBase-root": {
            height: 28,
            minHeight: 28,
            fontSize,
            borderRadius: 0.8,
            backgroundColor: "background.paper",
          },
          "& .MuiInputBase-input": {
            minWidth: 0,
            width: "100%",
            py: 0.35,
            px: 0.7,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.light",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
          },
        }}
      />
    );
  };

  /* ========================================================
             EXCEL EXPORT
             ======================================================== */

  const handleExportExcel = () => {
    if (filteredRows.length === 0) {
      setError("There is no data to export.");

      return;
    }

    const headerRow1: any[] = [
      "Person ID",
      "Person Name",
      "Employee Type",
      "Shift",
    ];

    const headerRow2: any[] = ["", "", "", ""];

    dayNumbers.forEach((day) => {
      headerRow1.push(String(day).padStart(2, "0"), "", "");

      headerRow2.push("In", "Out", "Status");
    });

    const exportRows = filteredRows.map((employee) => {
      const dayMap = new Map(employee.days.map((day) => [day.dayNumber, day]));

      const exportRow: any[] = [
        employee.personId,
        employee.personName,
        employee.employeeType,
        employee.shift,
      ];

      dayNumbers.forEach((day) => {
        const data = dayMap.get(day);

        exportRow.push(
          data?.inTime || "-",

          data?.outTime || "-",

          data?.status || "No Data",
        );
      });

      return exportRow;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
      ["Monthly Attendance Report"],
      ["Month", MONTHS[month - 1]],
      ["Year", year],
      [
        "Employee Type",
        employeeType === "all"
          ? "All Employee Types"
          : EMPLOYEE_TYPES.find((item) => item.value === employeeType)?.label ||
            employeeType,
      ],
      ["Shift", shift === "all" ? "All Shifts" : shift],
      [],
      headerRow1,
      headerRow2,
      ...exportRows,
    ]);

    const headerStartRow = 6;

    const mergeRanges: XLSX.Range[] = [
      {
        s: {
          r: headerStartRow,
          c: 0,
        },
        e: {
          r: headerStartRow + 1,
          c: 0,
        },
      },
      {
        s: {
          r: headerStartRow,
          c: 1,
        },
        e: {
          r: headerStartRow + 1,
          c: 1,
        },
      },
      {
        s: { r: headerStartRow, c: 2 },
        e: { r: headerStartRow + 1, c: 2 },
      },
      {
        s: { r: headerStartRow, c: 3 },
        e: { r: headerStartRow + 1, c: 3 },
      },
    ];

    dayNumbers.forEach((_, index) => {
      const startColumn = 4 + index * 3;

      mergeRanges.push({
        s: {
          r: headerStartRow,
          c: startColumn,
        },
        e: {
          r: headerStartRow,
          c: startColumn + 2,
        },
      });
    });

    worksheet["!merges"] = mergeRanges;

    worksheet["!cols"] = [
      {
        wch: 15,
      },
      {
        wch: 28,
      },
      {
        wch: 22,
      },
      {
        wch: 30,
      },
      ...dayNumbers.flatMap(() => [
        {
          wch: 12,
        },
        {
          wch: 12,
        },
        {
          wch: 14,
        },
      ]),
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Attendance");

    const employeeTypeFilePart = employeeType.replace(/\s+/g, "_");
    const shiftFilePart =
      shift === "all"
        ? "All_Shifts"
        : shift.replace(/[():]/g, "").replace(/\s+/g, "_");
    const fileName = `MonthlyAttendance_${
      MONTHS[month - 1]
    }_${year}_${employeeTypeFilePart}_${shiftFilePart}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  /* ========================================================
             BREADCRUMB
             ======================================================== */

  const breadcrumbItems = [
    {
      to: "/",
      title: "ASAI",
    },
    {
      title: "Monthly Attendance Report",
    },
  ];

  /* ========================================================
             RENDER
             ======================================================== */

  return (
    <PageContainer title="Monthly Attendance Report">
      <Breadcrumb title="Monthly Attendance Report" items={breadcrumbItems} />

      <Box
        sx={{
          width: "100%",
          px: {
            xs: 1,
            sm: 1.5,
            md: 2.5,
          },
          py: {
            xs: 1,
            md: 1.5,
          },
        }}
      >
        {/* ==================================================
                        FILTER BAR
                       ================================================== */}

        <Card
          elevation={0}
          sx={{
            mb: 1.5,
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 1.25,
                md: 1.5,
              },
              "&:last-child": {
                pb: {
                  xs: 1.25,
                  md: 1.5,
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: {
                  xs: "wrap",
                  xl: "nowrap",
                },
                width: "100%",
              }}
            >
              {/* ==================================================
        MONTH
       ================================================== */}

              <FormControl
                size="small"
                sx={{
                  width: {
                    xs: "100%",
                    sm: 180,
                    lg: 210,
                    xl: 220,
                  },
                  flexShrink: 0,
                }}
              >
                <InputLabel>Month</InputLabel>

                <Select
                  value={month}
                  label="Month"
                  onChange={(event) => setMonth(Number(event.target.value))}
                >
                  {MONTHS.map((monthName, index) => (
                    <MenuItem key={monthName} value={index + 1}>
                      {monthName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ==================================================
        EMPLOYEE TYPE
       ================================================== */}

              <FormControl
                size="small"
                sx={{
                  width: {
                    xs: "100%",
                    sm: 180,
                    lg: 200,
                    xl: 205,
                  },
                  flexShrink: 0,
                }}
              >
                <InputLabel>Employee Type</InputLabel>

                <Select
                  value={employeeType}
                  label="Employee Type"
                  onChange={(event: SelectChangeEvent) =>
                    setEmployeeType(event.target.value as EmployeeTypeFilter)
                  }
                >
                  <MenuItem value="all">All Employee Types</MenuItem>
                  {EMPLOYEE_TYPES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ==================================================
        SHIFT
       ================================================== */}

              <FormControl
                size="small"
                sx={{
                  width: {
                    xs: "100%",
                    sm: 220,
                    lg: 260,
                    xl: 285,
                  },
                  flexShrink: 0,
                }}
              >
                <InputLabel>Shift</InputLabel>

                <Select
                  value={shift}
                  label="Shift"
                  onChange={(event) => setShift(event.target.value)}
                >
                  <MenuItem value="all">All Shifts</MenuItem>
                  {shiftOptions.map((shiftName) => (
                    <MenuItem key={shiftName} value={shiftName}>
                      {shiftName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ==================================================
        YEAR
       ================================================== */}

              <TextField
                size="small"
                type="number"
                label="Year"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                sx={{
                  width: {
                    xs: "100%",
                    sm: 120,
                    lg: 125,
                    xl: 125,
                  },
                  flexShrink: 0,
                }}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayOutlined
                      sx={{
                        mr: 0.5,
                        fontSize: 17,
                        color: "text.secondary",
                      }}
                    />
                  ),
                }}
              />

              {/* ==================================================
        MAIN SEARCH
       ================================================== */}

              <Button
                size="small"
                variant="contained"
                onClick={handleLoad}
                disabled={loading}
                sx={{
                  minWidth: 88,
                  height: 40,
                  px: 1.5,
                  flexShrink: 0,
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Search
              </Button>

              {/* ==================================================
    RIGHT SIDE CONTROLS
   ================================================== */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  marginLeft: {
                    xs: 0,
                    xl: "auto",
                  },
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                {/* Global Employee Search */}
                <TextField
                  size="small"
                  placeholder="Search employee..."
                  value={searchText}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                    setPage(0);
                  }}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: 190,
                      lg: 200,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <SearchOutlined
                        sx={{
                          mr: 0.5,
                          fontSize: 18,
                        }}
                      />
                    ),
                  }}
                />

                {/* Active Column Filters */}
                {activeColumnFilterCount > 0 && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${activeColumnFilterCount} Filter${
                      activeColumnFilterCount === 1 ? "" : "s"
                    }`}
                    onDelete={clearAllColumnFilters}
                    sx={{
                      height: 32,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  />
                )}

                {/* Export Excel */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadOutlined />}
                  onClick={handleExportExcel}
                  sx={{
                    minWidth: 120,
                    height: 40,
                    px: 1.5,
                    flexShrink: 0,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Export Excel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "error.light",
              bgcolor: "error.lighter",
              color: "error.main",
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
              mb: 1.5,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        )}

        {!loading && rows.length > 0 && (
          <Card
            elevation={0}
            sx={{
              overflow: "hidden",
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 650,
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: tableMinWidth,

                  tableLayout: "auto",

                  "& .MuiTableCell-root": {
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.75rem",
                    py: 0.65,
                    px: 0.6,
                    whiteSpace: "nowrap",
                  },

                  "& .monthly-header-filter": {
                    transition: "width 0.2s ease, min-width 0.2s ease",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* PERSON ID */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        width: personIdWidth,
                        minWidth: personIdWidth,
                        maxWidth: personIdWidth,
                        position: "sticky",
                        left: 0,
                        zIndex: 8,
                        bgcolor: "background.paper",
                        fontWeight: 800,
                        verticalAlign: "top",
                        transition:
                          "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.45,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.3,
                            minHeight: 20,
                          }}
                        >
                          <span>Person ID</span>

                          <ColumnFilterButton
                            filterKey="personId"
                            label="Person ID"
                            size={12}
                          />
                        </Box>

                        {activeFilterKey === "personId" && (
                          <InlineFilterInput
                            filterKey="personId"
                            placeholder="Filter Person ID..."
                            width={personIdWidth - 12}
                            fontSize="0.68rem"
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* PERSON NAME */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        width: personNameWidth,
                        minWidth: personNameWidth,
                        maxWidth: personNameWidth,
                        position: "sticky",
                        left: personIdWidth,
                        zIndex: 8,
                        bgcolor: "background.paper",
                        fontWeight: 800,
                        verticalAlign: "top",
                        transition:
                          "left 0.2s ease, width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.45,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.3,
                            minHeight: 20,
                          }}
                        >
                          <span>Person Name</span>

                          <ColumnFilterButton
                            filterKey="personName"
                            label="Person Name"
                            size={12}
                          />
                        </Box>

                        {activeFilterKey === "personName" && (
                          <InlineFilterInput
                            filterKey="personName"
                            placeholder="Filter Person Name..."
                            width={personNameWidth - 12}
                            fontSize="0.68rem"
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* EMPLOYEE TYPE */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        width: employeeTypeWidth,
                        minWidth: employeeTypeWidth,
                        maxWidth: employeeTypeWidth + 40,
                        fontWeight: 800,
                        verticalAlign: "top",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.45,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.3,
                            minHeight: 20,
                          }}
                        >
                          <span>Employee Type</span>
                          <ColumnFilterButton
                            filterKey="employeeType"
                            label="Employee Type"
                            size={12}
                          />
                        </Box>
                        {activeFilterKey === "employeeType" && (
                          <InlineFilterInput
                            filterKey="employeeType"
                            placeholder="Filter type..."
                            width={employeeTypeWidth - 12}
                            fontSize="0.68rem"
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* SHIFT */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        width: shiftWidth,
                        minWidth: shiftWidth,
                        maxWidth: shiftWidth + 60,
                        fontWeight: 800,
                        verticalAlign: "top",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.45,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.3,
                            minHeight: 20,
                          }}
                        >
                          <span>Shift</span>
                          <ColumnFilterButton
                            filterKey="shift"
                            label="Shift"
                            size={12}
                          />
                        </Box>
                        {activeFilterKey === "shift" && (
                          <InlineFilterInput
                            filterKey="shift"
                            placeholder="Filter shift..."
                            width={shiftWidth - 12}
                            fontSize="0.68rem"
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* DAY GROUPS */}
                    {dayNumbers.map((day) => {
                      const groupWidth = getDayGroupWidth(day);

                      return (
                        <TableCell
                          key={day}
                          colSpan={3}
                          align="center"
                          className="monthly-header-filter"
                          sx={{
                            width: groupWidth,
                            minWidth: groupWidth,
                            bgcolor: "background.default",
                            fontWeight: 800,
                            transition: "width 0.2s ease, min-width 0.2s ease",
                          }}
                        >
                          {String(day).padStart(2, "0")}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {dayNumbers.map((day) => (
                      <React.Fragment key={`sub-${day}`}>
                        <TableCell
                          align="center"
                          className="monthly-header-filter"
                          sx={{
                            width: getDayFieldWidth(day, "in"),
                            minWidth: getDayFieldWidth(day, "in"),
                            maxWidth: getDayFieldWidth(day, "in"),
                            bgcolor: "background.default",
                            fontWeight: 700,
                            px: 0.25,
                            verticalAlign: "top",
                            transition:
                              "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.35,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.1,
                              }}
                            >
                              <span>In</span>

                              <ColumnFilterButton
                                filterKey={`day-${day}-in`}
                                label={`${String(day).padStart(2, "0")} - In`}
                                size={10}
                              />
                            </Box>

                            {activeFilterKey === `day-${day}-in` && (
                              <InlineFilterInput
                                filterKey={`day-${day}-in`}
                                placeholder="Filter In..."
                                width={getDayFieldWidth(day, "in") - 8}
                                fontSize="0.63rem"
                              />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell
                          align="center"
                          className="monthly-header-filter"
                          sx={{
                            width: getDayFieldWidth(day, "out"),
                            minWidth: getDayFieldWidth(day, "out"),
                            maxWidth: getDayFieldWidth(day, "out"),
                            bgcolor: "background.default",
                            fontWeight: 700,
                            px: 0.25,
                            verticalAlign: "top",
                            transition:
                              "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.35,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.1,
                              }}
                            >
                              <span>Out</span>

                              <ColumnFilterButton
                                filterKey={`day-${day}-out`}
                                label={`${String(day).padStart(2, "0")} - Out`}
                                size={10}
                              />
                            </Box>

                            {activeFilterKey === `day-${day}-out` && (
                              <InlineFilterInput
                                filterKey={`day-${day}-out`}
                                placeholder="Filter Out..."
                                width={getDayFieldWidth(day, "out") - 8}
                                fontSize="0.63rem"
                              />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell
                          align="center"
                          className="monthly-header-filter"
                          sx={{
                            width: getDayFieldWidth(day, "status"),
                            minWidth: getDayFieldWidth(day, "status"),
                            maxWidth: getDayFieldWidth(day, "status"),
                            bgcolor: "background.default",
                            fontWeight: 700,
                            px: 0.25,
                            verticalAlign: "top",
                            transition:
                              "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.35,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.1,
                              }}
                            >
                              <span>Status</span>

                              <ColumnFilterButton
                                filterKey={`day-${day}-status`}
                                label={`${String(day).padStart(
                                  2,
                                  "0",
                                )} - Status`}
                                size={10}
                              />
                            </Box>

                            {activeFilterKey === `day-${day}-status` && (
                              <InlineFilterInput
                                filterKey={`day-${day}-status`}
                                placeholder="Filter Status..."
                                width={getDayFieldWidth(day, "status") - 8}
                                fontSize="0.63rem"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pagedRows.map((employee, employeeIndex) => {
                    const dayMap = new Map<number, MonthlyAttendanceDay>(
                      employee.days.map((day) => [day.dayNumber, day]),
                    );

                    return (
                      <TableRow
                        hover
                        key={`${employee.personId}-${employeeIndex}`}
                      >
                        {/* PERSON ID */}
                        <TableCell
                          sx={{
                            width: personIdWidth,
                            minWidth: personIdWidth,
                            maxWidth: personIdWidth,
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            bgcolor: "background.paper",
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            transition:
                              "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                          }}
                        >
                          {employee.personId}
                        </TableCell>

                        {/* PERSON NAME */}
                        <TableCell
                          sx={{
                            width: personNameWidth,
                            minWidth: personNameWidth,
                            maxWidth: personNameWidth,
                            position: "sticky",
                            left: personIdWidth,
                            zIndex: 3,
                            bgcolor: "background.paper",
                            transition:
                              "left 0.2s ease, width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "action.hover",
                                color: "primary.main",
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(employee.personName)}
                            </Box>

                            <Box
                              sx={{
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <Typography
                                noWrap
                                fontWeight={700}
                                fontSize="0.78rem"
                              >
                                {employee.personName}
                              </Typography>

                              {employee.contractor &&
                                employee.contractor !== "-" && (
                                  <Typography
                                    noWrap
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {employee.contractor}
                                  </Typography>
                                )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ minWidth: employeeTypeWidth }}>
                          {employee.employeeType || "-"}
                        </TableCell>
                        <TableCell sx={{ minWidth: shiftWidth }}>
                          {employee.shift || "-"}
                        </TableCell>

                        {/* DAILY COLUMNS */}
                        {dayNumbers.map((day) => {
                          const dayData = dayMap.get(day);

                          return (
                            <React.Fragment key={`${employee.personId}-${day}`}>
                              {/* IN */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: getDayFieldWidth(day, "in"),
                                  minWidth: getDayFieldWidth(day, "in"),
                                  maxWidth: getDayFieldWidth(day, "in"),
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  transition:
                                    "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                                }}
                              >
                                {dayData?.inTime || "-"}
                              </TableCell>

                              {/* OUT */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: getDayFieldWidth(day, "out"),
                                  minWidth: getDayFieldWidth(day, "out"),
                                  maxWidth: getDayFieldWidth(day, "out"),
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  transition:
                                    "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                                }}
                              >
                                {dayData?.outTime || "-"}
                              </TableCell>

                              {/* STATUS */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: getDayFieldWidth(day, "status"),
                                  minWidth: getDayFieldWidth(day, "status"),
                                  maxWidth: getDayFieldWidth(day, "status"),
                                  overflow: "hidden",
                                  px: 0.25,
                                  transition:
                                    "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
                                }}
                              >
                                <Chip
                                  size="small"
                                  label={getStatusLabel(
                                    dayData?.status || "No Data",
                                  )}
                                  color={getStatusColor(
                                    dayData?.status || "No Data",
                                  )}
                                  variant="outlined"
                                  sx={{
                                    width: 50,
                                    maxWidth: 50,
                                    minWidth: 50,
                                    height: 20,
                                    borderRadius: "10px",
                                    fontSize: "0.62rem",
                                    overflow: "hidden",

                                    "& .MuiChip-label": {
                                      px: 0,
                                      width: "100%",
                                      textAlign: "center",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    );
                  })}

                  {/* EMPTY FILTER RESULT */}
                  {pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4 + daysInMonth * 3}
                        align="center"
                        sx={{
                          py: 5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No employees found for the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              showFirstButton
              showLastButton
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));

                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  minHeight: 44,
                },
              }}
            />
          </Card>
        )}

        {!loading && rows.length === 0 && !error && (
          <Box
            sx={{
              minHeight: 300,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.paper",
            }}
          >
            <CalendarTodayOutlined
              sx={{
                fontSize: 42,
                mb: 1,
                color: "text.secondary",
              }}
            />

            <Typography fontWeight={700}>Monthly Attendance Report</Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                textAlign: "center",
                px: 2,
              }}
            >
              Select month, year, employee type and shift, then click Search.
            </Typography>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};

export default EmployeeMonthlyAttendancePage;
